import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Truck, Shield, RotateCcw, ChevronRight, Star, Minus, Plus, Heart, Share2 } from "lucide-react";
import { http } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [storeSettings, setStoreSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("description");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      http.get(`/store/products/${id}`).then(r => { setProduct(r.data); setSelectedVariant(r.data.variants?.[0] || null); }).catch(() => {}),
      http.get("/store/settings").then(r => setStoreSettings(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="animate-pulse grid md:grid-cols-2 gap-10">
      <div className="aspect-square rounded-2xl bg-gray-100" />
      <div className="space-y-4"><div className="h-8 rounded bg-gray-100" /><div className="h-4 rounded bg-gray-100 w-2/3" /><div className="h-32 rounded bg-gray-100" /></div>
    </div>
  );

  if (!product) return (
    <div className="text-center py-20">
      <div className="text-lg font-semibold text-gray-500">Product not found</div>
      <Link to="/store/catalog" className="mt-4 inline-block text-sm font-semibold text-yellow-600">← Back to catalog</Link>
    </div>
  );

  const images = product.images || [];
  const currentVariant = selectedVariant;
  const price = Number(currentVariant?.price ?? product.price ?? 0);
  const comparePrice = Number(currentVariant?.compare_price ?? product.compare_price ?? 0);
  const stock = currentVariant?.stock ?? product.stock;
  const isOutOfStock = stock !== undefined && stock === 0;
  const currency = product.currency || storeSettings.currency || "$";

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    if (product.variants?.length > 0 && !selectedVariant) { toast.error("Please select an option"); return; }
    setAdding(true);
    addItem(product, selectedVariant, qty);
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setAdding(false), 1000);
  };

  const shippingLines = storeSettings.shipping_info_lines || ["Free standard shipping on orders over $50", "Express delivery available at checkout", "International shipping to selected countries"];

  return (
    <div className="space-y-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/store" className="hover:text-yellow-600">Store</Link>
        <ChevronRight size={13} />
        <Link to="/store/catalog" className="hover:text-yellow-600">Products</Link>
        {product.collection_name && (<><ChevronRight size={13} /><Link to={`/store/catalog?collection=${product.collection_slug}`} className="hover:text-yellow-600">{product.collection_name}</Link></>)}
        <ChevronRight size={13} />
        <span className="text-gray-900 font-medium truncate max-w-[160px]">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        {/* Image gallery */}
        <div className="space-y-3">
          <div className="relative rounded-2xl overflow-hidden bg-gray-50 aspect-square">
            {images.length > 0
              ? <img src={images[activeImage]} alt={product.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full grid place-items-center text-gray-300"><ShoppingCart size={64} /></div>
            }
            {product.badge && <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "#C5A028" }}>{product.badge}</span>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto scrollbar-none">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImage(i)} className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${activeImage === i ? "border-yellow-500" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product info */}
        <div className="space-y-6">
          {product.collection_name && (
            <div className="text-xs font-bold uppercase tracking-widest text-yellow-600">{product.collection_name}</div>
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{product.name}</h1>
            {product.sku && <div className="text-xs text-gray-400">SKU: {product.sku}</div>}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-bold text-gray-900">{currency}{price.toFixed(2)}</span>
            {comparePrice > 0 && comparePrice > price && (
              <>
                <span className="text-xl text-gray-400 line-through">{currency}{comparePrice.toFixed(2)}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: "#C5A028" }}>
                  Save {Math.round((1 - price / comparePrice) * 100)}%
                </span>
              </>
            )}
          </div>

          {/* Stock */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${isOutOfStock ? "bg-red-50 text-red-600" : stock !== undefined && stock < 10 ? "bg-orange-50 text-orange-600" : "bg-green-50 text-green-600"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOutOfStock ? "bg-red-500" : "bg-green-500"}`} />
              {isOutOfStock ? "Out of Stock" : stock !== undefined && stock < 10 ? `Only ${stock} left` : "In Stock"}
            </span>
          </div>

          {/* Variants */}
          {product.variants?.length > 0 && (() => {
            const groupKeys = [...new Set(product.variants.map(v => v.group || "Option"))];
            return groupKeys.map(group => {
              const groupVariants = product.variants.filter(v => (v.group || "Option") === group);
              return (
                <div key={group}>
                  <div className="text-sm font-semibold text-gray-700 mb-2">
                    {group}: <span className="font-normal text-gray-500">{selectedVariant?.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {groupVariants.map(v => (
                      <button
                        key={v.id}
                        onClick={() => setSelectedVariant(v)}
                        disabled={(v.stock !== undefined && v.stock === 0)}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all relative ${selectedVariant?.id === v.id ? "border-gray-900 text-gray-900" : "border-gray-200 text-gray-600 hover:border-gray-300"} ${(v.stock !== undefined && v.stock === 0) ? "opacity-40 cursor-not-allowed line-through" : ""}`}
                      >{v.label}</button>
                    ))}
                  </div>
                </div>
              );
            });
          })()}

          {/* Qty */}
          <div className="flex items-center gap-4">
            <div className="text-sm font-semibold text-gray-700">Quantity</div>
            <div className="flex items-center gap-1 border border-gray-200 rounded-xl overflow-hidden">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="p-2.5 hover:bg-gray-50 transition-colors"><Minus size={14} /></button>
              <span className="w-10 text-center text-sm font-bold">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="p-2.5 hover:bg-gray-50 transition-colors"><Plus size={14} /></button>
            </div>
          </div>

          {/* Add to cart + buy now */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock || adding}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "#0F1E38" }}
            >
              <ShoppingCart size={18} />
              {adding ? "Added to Cart ✓" : isOutOfStock ? "Out of Stock" : "Add to Cart"}
            </button>
            {!isOutOfStock && (
              <Link
                to="/store/checkout"
                onClick={() => { addItem(product, selectedVariant, qty); }}
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white transition-opacity"
                style={{ background: "#C5A028" }}
              >Buy Now</Link>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors"><Heart size={15} /> Save</button>
            <button onClick={() => navigator.share?.({ title: product.name, url: window.location.href })} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"><Share2 size={15} /> Share</button>
          </div>

          {/* Shipping info */}
          <div className="border-t border-gray-100 pt-6 space-y-3">
            {[{ icon: Truck, text: shippingLines[0] }, { icon: Shield, text: shippingLines[1] || "Secure payment" }, { icon: RotateCcw, text: shippingLines[2] || "Easy returns" }].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-gray-600">
                <Icon size={16} className="text-yellow-600 shrink-0" />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Product tabs */}
      <div className="border-t border-gray-100 pt-8">
        <div className="flex gap-6 border-b border-gray-100 mb-6">
          {["description", "details", "shipping", "reviews"].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`pb-3 text-sm font-semibold capitalize border-b-2 transition-colors ${tab === t ? "border-yellow-500 text-yellow-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>{t}</button>
          ))}
        </div>
        {tab === "description" && (
          <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">{product.description || "No description available."}</div>
        )}
        {tab === "details" && (
          <div className="space-y-2">
            {(product.details || []).map((d, i) => (
              <div key={i} className="flex items-start gap-3 text-sm"><span className="font-semibold text-gray-700 w-32 shrink-0">{d.label}</span><span className="text-gray-600">{d.value}</span></div>
            ))}
            {(!product.details || product.details.length === 0) && <div className="text-sm text-gray-500">No additional details available.</div>}
          </div>
        )}
        {tab === "shipping" && (
          <div className="space-y-3">
            {shippingLines.map((line, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-gray-700"><Truck size={15} className="text-yellow-600 shrink-0" />{line}</div>
            ))}
          </div>
        )}
        {tab === "reviews" && (
          <div className="text-center py-8 text-sm text-gray-500">
            {product.review_count > 0
              ? <div className="flex items-center justify-center gap-2"><Star className="text-yellow-400 fill-yellow-400" size={20} /><span className="font-bold text-lg">{product.avg_rating?.toFixed(1)}</span><span className="text-gray-400">({product.review_count} reviews)</span></div>
              : <div>No reviews yet. Be the first to review this product!</div>}
          </div>
        )}
      </div>
    </div>
  );
}
